import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import List from '@mui/material/List';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ApproverUserDto } from '@proposify/document-service-api-client';
import { useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useRequestApprovalMutation } from 'hooks/useRequestApprovalMutation';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import { Avatar } from '@proposify/ui';
import { getInitials } from 'utils/getInitials';

interface RequestApprovalMenuProps {
  contentId: string;
  eligibleApprovers: ApproverUserDto[];
}

export function RequestApprovalMenu({ contentId, eligibleApprovers }: RequestApprovalMenuProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [comment, setComment] = useState('');

  const { mutate: requestApproval, isPending } = useRequestApprovalMutation();

  const handleToggle = (event?: React.MouseEvent<HTMLElement>) => {
    if (anchorEl || !event) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSubmit = () => {
    requestApproval(
      { contentId, comment: comment.trim() || undefined },
      {
        onSuccess: () => {
          setComment('');
          setAnchorEl(null);
        },
      }
    );
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        className="approval-action-button"
        onClick={handleToggle}
        endIcon={anchorEl ? <ExpandLess /> : <ExpandMore />}
      >
        {t('share_document_v3.request_approval_button')}
      </Button>
      <Popover
        disableScrollLock
        data-testid="request-approval-menu"
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => handleToggle()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ '& .MuiPaper-root': { borderRadius: 4 } }}
      >
        <Card sx={{ minWidth: 344, maxWidth: 344 }}>
          <CardContent>
            <Stack gap={2}>
              <Stack>
                <Typography variant="h5" fontWeight={600}>
                  {t('share_document_v3.request_approval_menu.title')}
                </Typography>
                <Typography variant="body2" mt={0} color="text.secondary">
                  {t('share_document_v3.request_approval_menu.copy')}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  {t('share_document_v3.request_approval_menu.approvers_label')}
                </Typography>
                {eligibleApprovers.length ? (
                  <List disablePadding>
                    {eligibleApprovers.map((approver) => (
                      <ListItem key={approver.email} disableGutters disablePadding sx={{ minWidth: 0 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={(theme) => ({
                              width: 40,
                              height: 40,
                              fontSize: 20,
                              color: theme.palette.common.white,
                              backgroundColor: theme.palette.neutral.main,
                            })}
                          >
                            {getInitials(approver.firstName, approver.lastName)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ minWidth: 0 }}
                          primary={`${approver.firstName} ${approver.lastName}`.trim()}
                          secondary={approver.email}
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('share_document_v3.request_approval_menu.no_approvers')}
                  </Typography>
                )}
              </Stack>
              <TextField
                label={t('share_document_v3.request_approval_menu.comment_label')}
                placeholder={t('share_document_v3.request_approval_menu.comment_placeholder')}
                multiline
                rows={3}
                fullWidth
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isPending || eligibleApprovers.length === 0}>
                {t('share_document_v3.request_approval_menu.submit_button')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Popover>
    </>
  );
}
